import * as React from "react";
import { Account, CreateDocFunc, FileTags, MeterType, UploadedDocument } from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { DateInput } from "@/components/atoms/DateInput";
import { RadioGroupInput } from "@/components/atoms/RadioGroupInput";
import { FormField } from "@/components/molecules/FormField";
import { FormStack } from "@/components/molecules/FormStack";
import { InfoBoxTrigger } from "@/components/molecules/InfoBoxTrigger";
import { useOnValueChange, useUpdater } from "@/utils/callbacks";
import { SimpleUploadField } from "@/components/organisms/SimpleUploadField";
import { NestedErrors } from "@/utils/Types";

export type EnergyMeter = {
  readonly meterType?: MeterType;
  readonly lastMeterReadingDate?: string;
  readonly gasDocuments: UploadedDocument[];
  readonly electricityDocuments: UploadedDocument[];
};

export type Errors = NestedErrors<EnergyMeter> | undefined;

export const errorsOf: (r?: EnergyMeter) => Errors = (record) => {
  // const requiresReadings = record?.meterType === MeterType.Credit || record?.meterType === MeterType.PayAsYouGo;

  const requiresReadings = false;

  return cleanedErrors({
    meterType: record?.meterType ? undefined : "required",
    lastMeterReadingDate:
      !requiresReadings || record?.lastMeterReadingDate ? undefined : "required",
    gasFiles: !requiresReadings || (record?.gasDocuments || []).length > 0 ? undefined : "required",
    electricityFiles:
      !requiresReadings || (record?.electricityDocuments || []).length > 0 ? undefined : "required",
  });
};

export const recordFromAccount = (account: Account | undefined): EnergyMeter => {
  if (!account) {
    return {
      gasDocuments: [],
      electricityDocuments: [],
    };
  }

  return {
    meterType: account.meterType,
    lastMeterReadingDate: account.lastMeterReadingDate,
    gasDocuments:
      account.documents
        ?.filter(({ tags }) => tags?.includes(FileTags.GasMeterReading))
        .map(({ id, name }) => ({ id, filename: name })) || [],
    electricityDocuments:
      account.documents
        ?.filter(({ tags }) => tags?.includes(FileTags.ElectricityMeterReading))
        .map(({ id, name }) => ({ id, filename: name })) || [],
  };
};

export const accountFieldsFromRecord = (record?: EnergyMeter): Partial<Account> => {
  if (!record) return {};

  return {
    meterType: record?.meterType,
    lastMeterReadingDate: record?.lastMeterReadingDate,
    documentIds: [...record?.gasDocuments, ...record?.electricityDocuments].map((d) => d.id),
  };
};

export type EnergyMeterInfoFieldsProps = {
  record: EnergyMeter;
  errors?: any;
  update: any;
  readonly createDocument: CreateDocFunc;
  updateError: any;
  uploadedFileInfo: any;
};

export const EnergyMeterInfoFields: React.FC<EnergyMeterInfoFieldsProps> = ({
  record,
  errors,
  update,
  createDocument,
  updateError,
}) => {
  const onMeterTypeChange = useOnValueChange(update, "meterType");
  const onReadingDateChange = useOnValueChange(update, "lastMeterReadingDate");
  const updateGasMeterErrors = useUpdater(updateError, "gasDocuments");
  const updateEletricityMeterErrors = useUpdater(updateError, "electricityDocuments");

  const documentsUploader =
    (name: "gasDocuments" | "electricityDocuments") => (files: ReadonlyArray<File>) => {
      const tags = {
        gasDocuments: [FileTags.GasMeterReading],
        electricityDocuments: [FileTags.ElectricityMeterReading],
      }[name];

      const uploads = files.map((file) => {
        return createDocument({ file, fileTags: tags })
          .then(({ id, filename }) => {
            return { id, filename };
          })
          .catch((error) => {
            if (name === "gasDocuments") {
              updateGasMeterErrors(file.name + "  " + error.message);
              updateEletricityMeterErrors(undefined);
            }
            if (name === "electricityDocuments") {
              updateEletricityMeterErrors(file.name + "  " + error.message);
              updateGasMeterErrors(undefined);
            }
            return null;
          });
      });

      Promise.all(uploads).then((uploads) => {
        uploads = uploads.filter((u) => u !== null);
        update((record: EnergyMeter) => {
          return {
            ...record,
            [name]: [...(record[name] || []), ...uploads],
          };
        });
      });
    };

  const documentRemover = (name: "gasDocuments" | "electricityDocuments") => (id: string) => {
    update((record: EnergyMeter) => {
      return {
        ...record,
        [name]: record[name].filter((d) => d.id !== id),
      };
    });
  };
  return (
    <FormStack substack>
      <FormField label="Meter information">
        <RadioGroupInput
          onValueChange={onMeterTypeChange}
          value={record?.meterType}
          fields={meterTypeFields}
          error={errors?.meterType}
        />
      </FormField>

      {(record?.meterType === MeterType.Credit || record?.meterType === MeterType.PayAsYouGo) && (
        <FormStack substack>
          {" "}
          <FormField label="Date of the readings">
            {" "}
            <DateInput
              value={record?.lastMeterReadingDate || ""}
              onValueChange={onReadingDateChange}
              error={errors?.lastMeterReadingDate}
              pastOnly
            />{" "}
          </FormField>
          <FormField
            label={
              <span>
                If you have it, please upload a picture of the <b>gas</b> meter reading
              </span>
            }
          >
            {" "}
            <SimpleUploadField
              busy={false}
              entries={record?.gasDocuments}
              upload={documentsUploader("gasDocuments")}
              remove={documentRemover("gasDocuments")}
              error={errors?.gasDocuments}
              setError={updateGasMeterErrors}
            />
          </FormField>
          <FormField
            label={
              <span>
                If you have it, please upload a picture of the <b>electricity</b> meter reading
              </span>
            }
          >
            <SimpleUploadField
              busy={false}
              entries={record?.electricityDocuments}
              upload={documentsUploader("electricityDocuments")}
              remove={documentRemover("electricityDocuments")}
              error={errors?.electricityDocuments}
              setError={updateEletricityMeterErrors}
            />
          </FormField>{" "}
        </FormStack>
      )}
    </FormStack>
  );
};

const meterTypeFields = [
  { value: MeterType.SmartMeter, label: "Smart meter" },
  { value: MeterType.Credit, label: "Credit" },
  { value: MeterType.PayAsYouGo, label: "Pay as you go" },
  {
    value: MeterType.Unknown,
    label: (
      <>
        I don't know or I cannot provide a meter reading{" "}
        <InfoBoxTrigger
          content={
            <div>
              <p>
                <b>Don't have meter readings?</b> If you can't give us one today, the provider will
                use the previous meter reading as an estimate.
              </p>
            </div>
          }
        />
      </>
    ),
  },
];
